import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import Chart from 'react-apexcharts';
import ApexCharts from 'apexcharts';
import StatusChip from './StatusChip';

// Patch ApexCharts.prototype.destroy once so that it never throws when the
// underlying SVG DOM node has already been detached from the document.
// This covers React Strict Mode double-invoke, fast route transitions, and
// any other scenario where the chart cleanup races with DOM removal.
if (ApexCharts && ApexCharts.prototype && !ApexCharts.prototype._patchedDestroy) {
  const _originalDestroy = ApexCharts.prototype.destroy;
  ApexCharts.prototype.destroy = function () {
    try {
      _originalDestroy.call(this);
    } catch (_) { /* SVG node already detached – safe to ignore */ }
  };
  ApexCharts.prototype._patchedDestroy = true;
}

const CHART_IDS = ['status-chart', 'media-chart', 'trend-chart'];

// Aggregate raw daily trends by day (one point per day) for the trend chart. Safe to call with null/undefined.
function aggregateDailyTrendsByDay(dailyTrends) {
  const raw = dailyTrends || [];
  if (raw.length === 0) return { categories: [], total: [], completed: [], failed: [] };
  const byDay = {};
  raw.forEach((d) => {
    const id = d._id;
    const dayKey = typeof id === 'string' && id.includes(',') ? id.split(',')[0].trim() : String(id);
    if (!byDay[dayKey]) byDay[dayKey] = { count: 0, completed: 0, failed: 0 };
    byDay[dayKey].count += d.count || 0;
    byDay[dayKey].completed += d.completed || 0;
    byDay[dayKey].failed += d.failed || 0;
  });
  const sorted = Object.entries(byDay).sort((a, b) => {
    const parse = (s) => {
      const parts = s.split('/');
      if (parts.length === 3) return new Date(parts[2], Number(parts[0]) - 1, Number(parts[1])).getTime();
      return 0;
    };
    return parse(a[0]) - parse(b[0]);
  });
  const last30 = sorted.slice(-30);
  const formatShort = (s) => {
    const parts = s.split('/');
    if (parts.length === 3) {
      const d = new Date(parts[2], Number(parts[0]) - 1, Number(parts[1]));
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return s;
  };
  return {
    categories: last30.map(([day]) => formatShort(day)),
    total: last30.map(([, v]) => v.count),
    completed: last30.map(([, v]) => v.completed),
    failed: last30.map(([, v]) => v.failed),
  };
}

const EnhancedDashboard = ({ isAdmin = false }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    fetchAnalytics(fromDate, toDate);

    return () => {
      isMounted.current = false;
      // Proactively destroy all ApexCharts instances before React removes their
      // DOM nodes. Without this, ApexCharts' own componentWillUnmount fires
      // after the SVG is gone and crashes with "Cannot read properties of
      // undefined (reading 'node')".
      CHART_IDS.forEach(id => {
        try {
          ApexCharts.exec(id, 'destroy');
        } catch (_) { /* already destroyed or never mounted – safe to ignore */ }
      });
    };
  }, []); // initial mount

  const fetchAnalytics = async (from, to) => {
    console.log("=== ENHANCED DASHBOARD: Starting fetchAnalytics ===");
    console.log("BASE_URL:", BASE_URL);
    
    if (!BASE_URL) {
      console.error("⚠️ BASE_URL not defined! Using empty data");
      setAnalytics({
        statusBreakdown: {},
        mediaTypeBreakdown: {},
        dailyTrends: [],
        totalMessages: 0,
        successRate: 0,
        totalCampaigns: 0
      });
      setLoading(false);
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      const url = `${BASE_URL}admin/getCampaignAnalytics`;
      
      console.log("Making API request to:", url);
      
      const payload = {};
      if (from) payload.fromDate = from;
      if (to) payload.toDate = to;

      const response = await axios.post(url, payload, {
        headers: {
          "x-access-token": token,
          "Content-Type": "application/json",
          version: "1.0.0",
        },
        timeout: 8000
      });
      
      console.log("✓ API Response:", response.status);
      console.log("✓ Success:", response.data.success);
      
      if (response.data.success && isMounted.current) {
        console.log("✓ Setting analytics data");
        setAnalytics(response.data.data);
      }
    } catch (error) {
      console.error("✗ API Error:", error.message);
      
      if (isMounted.current) {
        setAnalytics({
          statusBreakdown: {},
          mediaTypeBreakdown: {},
          dailyTrends: [],
          totalMessages: 0,
          successRate: 0,
          totalCampaigns: 0
        });
      }
    } finally {
      console.log("✓ Setting loading=false");
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-center align-items-center" style={{ height: '300px', flexDirection: 'column' }}>
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted">Loading analytics...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!analytics) {
    console.log("=== NO ANALYTICS DATA YET ===");
    return null;
  }

  // Ensure data exists
  const hasStatusData = analytics.statusBreakdown && Object.keys(analytics.statusBreakdown).length > 0;
  const hasMediaData = analytics.mediaTypeBreakdown && Object.keys(analytics.mediaTypeBreakdown).length > 0;
  const dailyByDay = aggregateDailyTrendsByDay(analytics.dailyTrends);
  const hasTrendData = dailyByDay.categories.length > 0;

  // Status Breakdown Donut Chart – fixed colors per status
  const statusLabels = hasStatusData ? Object.keys(analytics.statusBreakdown) : [];
  const statusColorMap = {
    Complete: '#3AC977', // green
    Completed: '#3AC977',
    Process: '#0D99FF', // blue
    Processing: '#0D99FF',
    Pending: '#FF9F00', // yellow
    Failed: '#F86C6B', // red
    Unknown: '#6c757d'
  };
  const statusColors = statusLabels.map(label => statusColorMap[label] || '#6c757d');

  // Status Breakdown Donut Chart
  const statusChartOptions = {
    chart: { type: 'donut', toolbar: { show: false }, id: 'status-chart' },
    labels: statusLabels,
    colors: statusColors,
    legend: { position: 'bottom' },
    dataLabels: { enabled: true },
    responsive: [{
      breakpoint: 480,
      options: {
        chart: { width: 200 },
        legend: { position: 'bottom' }
      }
    }],
    noData: {
      text: 'No data available',
      align: 'center',
      verticalAlign: 'middle'
    }
  };

  const statusChartSeries = hasStatusData ? Object.values(analytics.statusBreakdown) : [];

  // Media Type Bar Chart
  const mediaChartOptions = {
    chart: { type: 'bar', toolbar: { show: false }, id: 'media-chart' },
    plotOptions: {
      bar: { horizontal: false, columnWidth: '55%', borderRadius: 8 }
    },
    dataLabels: { enabled: false },
    xaxis: { categories: hasMediaData ? Object.keys(analytics.mediaTypeBreakdown) : [] },
    colors: ['#0D99FF'],
    grid: { borderColor: '#f1f1f1' },
    noData: {
      text: 'No data available',
      align: 'center',
      verticalAlign: 'middle'
    }
  };

  const mediaChartSeries = [{
    name: 'Campaigns',
    data: hasMediaData ? Object.values(analytics.mediaTypeBreakdown) : []
  }];

  // Daily Trends: one point per day, proper Y-scale (counts), readable X labels
  const dailyTrendOptions = {
    chart: { type: 'area', toolbar: { show: false }, sparkline: { enabled: false }, id: 'trend-chart' },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 2 },
    xaxis: {
      categories: dailyByDay.categories,
      labels: { show: true, rotate: -45, maxHeight: 60 },
      tickAmount: Math.min(12, dailyByDay.categories.length),
      tooltip: { enabled: true }
    },
    yaxis: {
      title: { text: 'Campaigns' },
      labels: { formatter: (v) => Math.round(v) },
      min: 0,
      forceNiceScale: true
    },
    colors: ['#0D99FF', '#3AC977', '#F86C6B'],
    legend: { position: 'top', horizontalAlign: 'right' },
    grid: { borderColor: '#f1f1f1' },
    noData: {
      text: 'No data available',
      align: 'center',
      verticalAlign: 'middle'
    }
  };

  const dailyTrendSeries = hasTrendData ? [
    { name: 'Total', data: dailyByDay.total },
    { name: 'Completed', data: dailyByDay.completed },
    { name: 'Failed', data: dailyByDay.failed }
  ] : [];

  console.log("✓ Rendering dashboard with data:", { hasStatusData, hasMediaData, hasTrendData });

  return (
    <div className="row">
      {/* KPI Cards */}
      <div className="col-xl-3 col-sm-6">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h6 className="text-muted mb-1">Total Campaigns</h6>
                <h3 className="mb-0">{analytics.totalCampaigns}</h3>
              </div>
              <div className="icon-box bg-primary-light">
                <i className="fa fa-bullhorn" style={{ fontSize: '24px', color: 'var(--primary)' }}></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="col-xl-3 col-sm-6">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h6 className="text-muted mb-1">Total Messages</h6>
                <h3 className="mb-0">{analytics.totalMessages.toLocaleString()}</h3>
              </div>
              <div className="icon-box bg-success-light">
                <i className="fa fa-envelope" style={{ fontSize: '24px', color: '#3AC977' }}></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="col-xl-3 col-sm-6">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h6 className="text-muted mb-1">Success Rate</h6>
                <h3 className="mb-0">{analytics.successRate}%</h3>
              </div>
              <div className="icon-box bg-success-light">
                <i className="fa fa-check-circle" style={{ fontSize: '24px', color: '#3AC977' }}></i>
              </div>
            </div>
            <div className="progress mt-3" style={{ height: '6px' }}>
              <div 
                className="progress-bar bg-success" 
                role="progressbar" 
                style={{ width: `${analytics.successRate}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      <div className="col-xl-3 col-sm-6">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h6 className="text-muted mb-1">Completed</h6>
                <h3 className="mb-0">{analytics.statusBreakdown.Complete || 0}</h3>
              </div>
              <div className="icon-box bg-success-light">
                <i className="fa fa-trophy" style={{ fontSize: '24px', color: '#3AC977' }}></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="col-xl-6 col-lg-6">
        <div className="card">
          <div className="card-header border-0">
            <h4 className="heading mb-0">Campaign Status Distribution</h4>
          </div>
          <div className="card-body">
            {hasStatusData ? (
              <Chart
                options={statusChartOptions}
                series={statusChartSeries}
                type="donut"
                height={300}
              />
            ) : (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                <p className="text-muted">No campaign data available</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="col-xl-6 col-lg-6">
        <div className="card">
          <div className="card-header border-0">
            <h4 className="heading mb-0">Media Type Performance</h4>
          </div>
          <div className="card-body">
            {hasMediaData ? (
              <Chart
                options={mediaChartOptions}
                series={mediaChartSeries}
                type="bar"
                height={300}
              />
            ) : (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                <p className="text-muted">No media data available</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Daily Trends */}
      <div className="col-xl-12">
        <div className="card">
          <div className="card-header border-0">
            <h4 className="heading mb-0">Campaign Trends (Last 30 Days)</h4>
          </div>
          <div className="card-body">
            {hasTrendData ? (
              <Chart
                options={dailyTrendOptions}
                series={dailyTrendSeries}
                type="area"
                height={350}
              />
            ) : (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '350px' }}>
                <p className="text-muted">No trend data available</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedDashboard;
